import type { OfferedResource, Resource } from '../../api/types';
import { useI18n } from '../../i18n/useI18n';
import { Ago } from '../../ui/Ago';
import { Button, Labels } from '../../ui/primitives';
import { Drawer } from '../../ui/Drawer';
import { KindIcon } from '../../ui/KindIcon';
import { StatusBadge } from '../../ui/StatusBadge';
import { kindLabelKey } from '../../ui/ResourceTable';

/**
 * One resource, whole, in a panel beside the list it came from.
 *
 * The table shows six things because six is what fits and what is scanned; this
 * shows everything the catalogue holds about a row, in the order somebody asks
 * for it: what it is, where it is, who serves it, what policy reads off it.
 *
 * **Nothing here is editable.** The two things that can be done to a resource
 * are at the foot, and pressing edit turns this same panel into the form —
 * rather than opening a dialog over it, which would put the same row on the
 * screen twice and leave a reader deciding which one is the truth.
 */
export function ResourceDrawer({
  resource,
  offer,
  serving,
  mayWrite,
  onEdit,
  onRemove,
  onClose,
}: {
  resource: Resource;
  /** What a connector says it serves under this id, when one does. */
  offer?: OfferedResource;
  /** Whether the gateway is routing to it now; absent when nobody asked. */
  serving?: boolean;
  mayWrite: boolean;
  onEdit: () => void;
  onRemove: () => void;
  onClose: () => void;
}) {
  const { t, formatNumber } = useI18n();
  const id = resource.id ?? resource.hostname ?? '';
  const kindLabel = kindLabelKey(resource.kind);
  const address =
    resource.port === undefined
      ? (resource.hostname ?? id)
      : `${resource.hostname ?? id}:${String(resource.port)}`;

  return (
    <Drawer
      title={id}
      subtitle={address}
      onClose={onClose}
      footer={
        mayWrite ? (
          <>
            <Button variant="primary" onClick={onEdit}>
              {t('catalog.edit')}
            </Button>
            <span className="spacer" />
            <Button variant="danger" onClick={onRemove}>
              {t('catalog.remove')}
            </Button>
          </>
        ) : undefined
      }
    >
      <dl className="dl">
        <dt>{t('catalog.col.kind')}</dt>
        <dd>
          <span className="chip">
            <KindIcon kind={resource.kind} size={13} />{' '}
            {kindLabel === null ? resource.kind : t(kindLabel)}
          </span>
        </dd>

        <dt>{t('catalog.col.address')}</dt>
        <dd className="mono">{address}</dd>

        {resource.parent !== undefined && (
          <>
            <dt>{t('resource.field.parent')}</dt>
            <dd className="mono">{resource.parent}</dd>
          </>
        )}

        {resource.aliases !== undefined && resource.aliases.length > 0 && (
          <>
            <dt>{t('resource.field.aliases')}</dt>
            <dd className="mono">{resource.aliases.join(', ')}</dd>
          </>
        )}

        <dt>{t('catalog.col.project')}</dt>
        <dd className="mono">{resource.project}</dd>

        <dt>{t('catalog.col.environment')}</dt>
        <dd className="mono">{resource.environment}</dd>

        <dt>{t('catalog.col.approvers')}</dt>
        <dd>
          {resource.min_approvers === undefined
            ? t('catalog.approvers.inherited')
            : formatNumber(resource.min_approvers)}
        </dd>

        {resource.kind === 'mcp' && (
          <>
            <dt>{t('resource.pin.label')}</dt>
            <dd>{t(resource.pin_tool_descriptors === true ? 'common.yes' : 'common.no')}</dd>
          </>
        )}

        {/* Only shown where there is one. An application with no name of its
            own outside the cluster is the ordinary case, and a row reading
            "none" would be answering a question nobody asked. */}
        {resource.native_name !== undefined && (
          <>
            <dt>{t('resource.field.nativeName')}</dt>
            <dd className="mono">{resource.native_name}</dd>
          </>
        )}

        <dt>{t('catalog.col.served')}</dt>
        <dd>
          {serving === undefined ? (
            <span className="muted" title={t('catalog.served.unknown.hint')}>
              {t('catalog.served.unknown')}
            </span>
          ) : serving ? (
            <StatusBadge tone="ok" label={t('catalog.served.yes')} />
          ) : (
            <StatusBadge
              tone="deny"
              label={t('catalog.served.no')}
              hint={t('catalog.served.no.hint')}
            />
          )}
        </dd>

        {offer !== undefined && (
          <>
            <dt>{t('catalog.col.connector')}</dt>
            <dd className="mono">{offer.claimed_name ?? offer.node_id}</dd>

            <dt>{t('offered.col.offered')}</dt>
            <dd>
              <Ago at={new Date(offer.offered_at * 1000)} />
            </dd>
          </>
        )}
      </dl>

      <div style={{ marginTop: 18 }}>
        <Labels labels={resource.labels} empty={t('catalog.noLabels')} />
      </div>
    </Drawer>
  );
}
